import { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  CheckCircle2,
  Layers,
  Loader2,
  Search,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import {
  consolidateBalanceDuplicates,
  DuplicateConsolidationResult,
  DuplicatePreview,
  previewBalanceDuplicates,
} from "@/Api/balanceAdjustments";
import { getSelectSellers } from "@/Api/sellers";
import { ContestOption, SellerOption, toOptions, toSellerOptions } from "../types";

/**
 * Consolidação de linhas duplicadas do balanço de VENDEDOR.
 *
 * Fluxo: vendedor + concurso -> pesquisar -> pré-visualizar -> confirmar ->
 * consolidar -> resumo validado.
 *
 * O backend só aceita a consolidação com o `preview_hash` desta pesquisa; se
 * qualquer saldo mudar nesse intervalo, a operação é recusada.
 */
export const DuplicatesTab = ({ contests }: { contests: ContestOption[] }) => {
  const [sellers, setSellers] = useState<SellerOption[]>([]);
  const [sellerIds, setSellerIds] = useState<string[]>([]);
  const [contestIds, setContestIds] = useState<string[]>([]);

  const [isSearching, setIsSearching] = useState(false);
  const [isConsolidating, setIsConsolidating] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const [preview, setPreview] = useState<DuplicatePreview | null>(null);
  const [result, setResult] = useState<DuplicateConsolidationResult | null>(null);

  useEffect(() => {
    const loadSellers = async () => {
      const response = await getSelectSellers({});
      if (response.ok) setSellers(response.data?.response ?? []);
    };

    loadSellers();
  }, []);

  const sellerOptions = useMemo(() => toSellerOptions(sellers), [sellers]);

  const sellerId = sellerIds[0];
  const contestId = contestIds[0];
  const canSearch = Boolean(sellerId && contestId) && !isSearching;

  /** Seleção única: mantém apenas o último item marcado. */
  const handleSingleSelect =
    (setter: (values: string[]) => void, current: string[]) => (values: string[]) => {
      const added = values.find((value) => !current.includes(value));
      setter(added ? [added] : values.slice(0, 1));
      setPreview(null);
      setResult(null);
      setIsConfirming(false);
    };

  const handleSearch = async () => {
    if (!sellerId || !contestId) return;

    setIsSearching(true);
    setResult(null);
    setIsConfirming(false);

    const response = await previewBalanceDuplicates(sellerId, contestId);

    setIsSearching(false);

    if (!response.ok) {
      setPreview(null);
      return;
    }

    const data: DuplicatePreview = response.data?.response;
    setPreview(data);

    if (data.groups_found === 0) {
      toast.success("Nenhuma duplicidade encontrada para este vendedor e concurso.");
    }
  };

  const handleConsolidate = async () => {
    if (!preview || !sellerId || !contestId) return;

    setIsConsolidating(true);

    const response = await consolidateBalanceDuplicates({
      seller_id: sellerId,
      contest_id: contestId,
      preview_hash: preview.preview_hash,
      confirmed: true,
    });

    setIsConsolidating(false);
    setIsConfirming(false);

    if (!response.ok) {
      /** A pré-visualização pode ter ficado obsoleta - força nova pesquisa. */
      setPreview(null);
      return;
    }

    setResult(response.data?.response);
    setPreview(null);
    toast.success("Consolidação concluída e validada.");
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Filtros</CardTitle>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Vendedor</Label>
              <MultiSelectCheckbox
                options={sellerOptions}
                selectedValues={sellerIds}
                hideBadge
                onSelectionChange={handleSingleSelect(setSellerIds, sellerIds)}
                placeholder="Selecione o vendedor"
                searchable
                searchPlaceholder="Buscar vendedor..."
              />
            </div>

            <div className="space-y-1">
              <Label>Concurso</Label>
              <MultiSelectCheckbox
                options={toOptions(contests)}
                selectedValues={contestIds}
                hideBadge
                onSelectionChange={handleSingleSelect(setContestIds, contestIds)}
                placeholder="Selecione o concurso"
                searchable
                searchPlaceholder="Buscar concurso..."
              />
            </div>
          </div>

          <div className="flex justify-end border-t pt-4">
            <Button size="sm" onClick={handleSearch} disabled={!canSearch}>
              {isSearching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              Pesquisar duplicidades
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && preview.warnings.length > 0 && (
        <div className="flex flex-col gap-2">
          {preview.warnings.map((warning) => (
            <div
              key={warning}
              className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{warning}</span>
            </div>
          ))}
        </div>
      )}

      {preview && preview.groups_found === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <p className="font-medium">Nenhuma duplicidade encontrada</p>
            <p className="text-sm text-muted-foreground">
              {preview.seller.name} — concurso {preview.contest.name}
            </p>
          </CardContent>
        </Card>
      )}

      {preview && preview.groups_found > 0 && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">
                  Pré-visualização da consolidação
                </CardTitle>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="secondary">
                  {preview.groups_found}{" "}
                  {preview.groups_found === 1 ? "grupo" : "grupos"}
                </Badge>
                <Badge variant="secondary">{preview.rows_involved} linhas</Badge>
                <Badge variant="outline">
                  {preview.rows_to_remove} serão inativadas
                </Badge>
              </div>
            </div>
            <p className="pt-1 text-sm text-muted-foreground">
              {preview.seller.name}
              {preview.seller.unity_name ? ` • ${preview.seller.unity_name}` : ""} —
              concurso {preview.contest.name}
            </p>
          </CardHeader>

          <CardContent className="space-y-4">
            {preview.groups.map((group, index) => (
              <div key={group.cp_classification_id} className="rounded-md border">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 px-4 py-2">
                  <span className="text-sm font-medium">
                    Grupo {index + 1} — {group.product_name}
                    {group.product_classification_name
                      ? ` (${group.product_classification_name})`
                      : ""}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Total: <strong>{group.total_balance}</strong> de balanço /{" "}
                    <strong>{group.total_with_held_balance}</strong> preso
                  </span>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Linha</TableHead>
                      <TableHead>Criada em</TableHead>
                      <TableHead className="text-right">Balanço</TableHead>
                      <TableHead className="text-right">Valor preso</TableHead>
                      <TableHead className="text-right">Destino</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.rows.map((row) => (
                      <TableRow
                        key={row.id}
                        className={row.will_keep ? "bg-emerald-50 dark:bg-emerald-950/30" : ""}
                      >
                        <TableCell className="font-mono text-xs">#{row.id}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {row.created_at ?? "-"}
                        </TableCell>
                        <TableCell className="text-right">{row.balance}</TableCell>
                        <TableCell className="text-right">
                          {row.with_held_balance}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.will_keep ? (
                            <Badge className="bg-emerald-600 hover:bg-emerald-600">
                              Mantida (mais recente)
                            </Badge>
                          ) : (
                            <Badge variant="outline">Zerada e inativada</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}

            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              A soma de cada grupo vai para a linha mais recente — a mesma que venda,
              distribuição e pré-venda utilizam. As demais são zeradas e inativadas, e
              as distribuições e fluxos que apontavam para elas passam a apontar para a
              linha mantida. O total do vendedor no concurso não muda:{" "}
              <strong>{preview.totals_before.balance}</strong> de balanço /{" "}
              <strong>{preview.totals_before.with_held_balance}</strong> preso.
              <br />
              <br />
              Execute preferencialmente em um momento sem vendas ou distribuições em
              andamento para este vendedor: os fluxos de venda e distribuição gravam o
              saldo por valor absoluto e podem sobrescrever a consolidação se
              ocorrerem no mesmo instante.
            </div>

            {isConfirming ? (
              <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
                <span className="mr-auto text-sm font-medium">
                  Confirma a consolidação de {preview.groups_found}{" "}
                  {preview.groups_found === 1 ? "grupo" : "grupos"}?
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsConfirming(false)}
                  disabled={isConsolidating}
                >
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleConsolidate} disabled={isConsolidating}>
                  {isConsolidating && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Confirmar consolidação
                </Button>
              </div>
            ) : (
              <div className="flex justify-end border-t pt-4">
                <Button size="sm" onClick={() => setIsConfirming(true)}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Consolidar duplicidades
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {result && (
        <Card className="border-emerald-300 dark:border-emerald-800">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <CardTitle className="text-base">Resumo da operação</CardTitle>
            </div>
            <p className="pt-1 text-sm text-muted-foreground">
              {result.seller.name} — concurso {result.contest.name}
            </p>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm lg:grid-cols-4">
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Grupos consolidados</div>
                <div className="text-lg font-semibold">{result.groups_consolidated}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Linhas consolidadas</div>
                <div className="text-lg font-semibold">{result.rows_consolidated}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Total antes</div>
                <div className="text-lg font-semibold">
                  {result.totals_before.balance} / {result.totals_before.with_held_balance}
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Total depois</div>
                <div className="text-lg font-semibold">
                  {result.totals_after.balance} / {result.totals_after.with_held_balance}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-md border">
                <div className="border-b bg-muted/40 px-4 py-2 text-sm font-medium">
                  Linhas mantidas ({result.rows_kept.length})
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bolão</TableHead>
                      <TableHead>Linha</TableHead>
                      <TableHead className="text-right">Balanço</TableHead>
                      <TableHead className="text-right">Preso</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.rows_kept.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.product_name}</TableCell>
                        <TableCell className="font-mono text-xs">#{row.id}</TableCell>
                        <TableCell className="text-right">{row.balance}</TableCell>
                        <TableCell className="text-right">
                          {row.with_held_balance}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="rounded-md border">
                <div className="border-b bg-muted/40 px-4 py-2 text-sm font-medium">
                  Linhas inativadas ({result.rows_removed.length})
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bolão</TableHead>
                      <TableHead>Linha</TableHead>
                      <TableHead className="text-right">Balanço anterior</TableHead>
                      <TableHead className="text-right">Preso anterior</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.rows_removed.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.product_name}</TableCell>
                        <TableCell className="font-mono text-xs">#{row.id}</TableCell>
                        <TableCell className="text-right">
                          {row.previous_balance}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.previous_with_held_balance}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              {result.distributions_repointed} distribuição(ões) e{" "}
              {result.balance_flows_repointed} fluxo(s) de balanço repontado(s) para a
              linha mantida. Validação pós-correção:{" "}
              <strong className="text-emerald-700 dark:text-emerald-400">
                aprovada
              </strong>
              . O detalhe de cada alteração está na aba Auditoria.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
